// Resolves the ids Brahma reported for a response into named, linkable sources.

import { useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { useMemo } from 'react';
import { useKnowledgeBasesByIds, useRulesByIds } from '@/hooks/use-shadow-metadata';
import { sourceService } from '@/services/source.service';
import type { GeneratedResponse, ResponseSource } from '@/modules/sentinel-workspace/types';

/** Sources have no by-ids endpoint, so the account's list is fetched once and matched locally. */
const sourcesQueryAtom = atomWithQuery(() => ({
  queryKey: ['sources', 'all'],
  queryFn: () => sourceService.getSources(),
  staleTime: 5 * 60_000,
}));

/** Only some sources carry an excerpt or a page to open; the rest render as a plain chip. */
function asSource(source: Pick<ResponseSource, 'id' | 'title' | 'url'> & { excerpt?: string }): ResponseSource {
  return { fileType: '', excerpt: '', ...source };
}

/** Names come from our own stores; Brahma only reports which ids it used. */
export function useResponseSources(response: Pick<GeneratedResponse, 'knowledgeBaseIds' | 'ruleIds'>): ResponseSource[] {
  const knowledgeBases = useKnowledgeBasesByIds(response.knowledgeBaseIds);
  const rules = useRulesByIds(response.ruleIds);
  const [sources] = useAtom(sourcesQueryAtom);

  return useMemo(() => {
    const sourceById = new Map((sources.data ?? []).map((source) => [source.id, source]));

    // Uploaded sources arrive inside the knowledge base list, so match those ids first.
    const fromSources = response.knowledgeBaseIds.flatMap((id) => {
      const source = sourceById.get(id);
      if (!source) return [];
      return [asSource({ id, title: source.name, url: `/training/sources/${id}` })];
    });

    // What is left are knowledge base articles, which have no page to link to yet.
    const fromArticles = (knowledgeBases.data ?? [])
      .filter((article) => !sourceById.has(article.id))
      .map((article) =>
        asSource({ id: article.id, title: article.name, excerpt: article.description || article.content, url: null }),
      );

    const fromWorkflows = (rules.data ?? []).map((rule) =>
      asSource({
        id: rule.id,
        title: rule.name,
        excerpt: rule.description ?? '',
        url: `/training/workflows/v2/${rule.id}`,
      }),
    );

    return [...fromSources, ...fromArticles, ...fromWorkflows];
  }, [knowledgeBases.data, response.knowledgeBaseIds, rules.data, sources.data]);
}
